import React, { useState, useRef } from 'react';
import { Mail, X, Eye, FileEdit, Send, Loader2, CheckCircle } from 'lucide-react';
import DOMPurify from 'dompurify';

import { copiesError, formatCopies, parseCopies } from '../../lib/deliverables/meetingReportSend';

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (to: string, cc: string[], subject: string, html: string) => Promise<{ success: boolean; error?: string }>;
  initialTo: string;
  /** The copies offered (a CR: the meeting's external participants, from the server's send state). */
  initialCc: readonly string[];
  initialSubject: string;
  initialHtml: string;
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  isOpen,
  onClose,
  onSend,
  initialTo,
  initialCc,
  initialSubject,
  initialHtml,
}) => {
  // A string, not the array: the reset below must not fire on every render of the caller.
  const initialCcText = formatCopies(initialCc);
  const [emailTo, setEmailTo] = useState(initialTo);
  const [emailCc, setEmailCc] = useState(initialCcText);
  const [emailSubject, setEmailSubject] = useState(initialSubject);
  const [editing, setEditing] = useState(false);
  const [html, setHtml] = useState(initialHtml);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editableRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens with new values
  React.useEffect(() => {
    if (isOpen) {
      setEmailTo(initialTo);
      setEmailCc(initialCcText);
      setEmailSubject(initialSubject);
      setHtml(initialHtml);
      setEditing(false);
      setSending(false);
      setSent(false);
      setError(null);
    }
  }, [isOpen, initialTo, initialCcText, initialSubject, initialHtml]);

  if (!isOpen) return null;

  const ccError = copiesError(emailCc);

  const handleToggleEdit = () => {
    if (editing && editableRef.current) {
      setHtml(editableRef.current.innerHTML);
    }
    setEditing(!editing);
  };

  const handleSend = async () => {
    if (!emailTo || ccError || sending) return;
    setError(null);
    setSending(true);

    const finalHtml = (editing && editableRef.current)
      ? editableRef.current.innerHTML
      : html;

    const result = await onSend(emailTo, parseCopies(emailCc).emails, emailSubject, finalHtml);

    setSending(false);
    if (result.success) {
      setSent(true);
      setTimeout(() => onClose(), 1500);
    } else {
      setError(result.error || "Erreur lors de l'envoi");
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[640px] max-h-[85vh] flex flex-col overflow-hidden border border-neutral-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-purple-50 border-b border-purple-200">
          <div className="flex items-center gap-2 text-purple-800 font-semibold text-sm">
            <Mail className="w-4 h-4" />
            Aperçu de l'email
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Fields */}
        <div className="px-5 py-3 space-y-2.5 border-b border-neutral-200 bg-neutral-50">
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-neutral-500 w-10">À</label>
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              disabled={sending || sent}
              className="flex-1 text-sm px-3 py-1.5 border border-neutral-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none disabled:opacity-60"
            />
          </div>
          <div className="flex items-center gap-3">
            <label htmlFor="email-preview-cc" className="text-xs font-medium text-neutral-500 w-10">Copie</label>
            <input
              id="email-preview-cc"
              type="text"
              value={emailCc}
              onChange={(e) => setEmailCc(e.target.value)}
              disabled={sending || sent}
              placeholder="Aucune copie"
              aria-invalid={ccError !== null}
              className="flex-1 text-sm px-3 py-1.5 border border-neutral-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none disabled:opacity-60"
            />
          </div>
          {ccError && <p className="pl-[3.25rem] text-xs text-red-600">{ccError}</p>}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-neutral-500 w-10">Objet</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              disabled={sending || sent}
              className="flex-1 text-sm px-3 py-1.5 border border-neutral-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none disabled:opacity-60"
            />
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-1.5 border-b border-neutral-200 bg-white">
          <button
            onClick={handleToggleEdit}
            disabled={sending || sent}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              editing
                ? 'bg-purple-100 text-purple-700'
                : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
            } disabled:opacity-50`}
          >
            {editing ? <Eye className="w-3.5 h-3.5" /> : <FileEdit className="w-3.5 h-3.5" />}
            {editing ? 'Aperçu' : 'Modifier le contenu'}
          </button>
          {editing && (
            <span className="text-[10px] text-amber-600 font-medium">Mode édition</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div
            ref={editing ? editableRef : undefined}
            contentEditable={editing && !sending && !sent}
            suppressContentEditableWarning
            data-sensitive-text=""
            className={`px-5 py-4 prose prose-sm max-w-none text-neutral-800 outline-none
              [&_h1]:text-lg [&_h1]:font-bold [&_h1]:text-neutral-900 [&_h1]:mb-3
              [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-neutral-800 [&_h2]:border-b [&_h2]:border-neutral-200 [&_h2]:pb-1.5 [&_h2]:mt-5 [&_h2]:mb-2
              [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-neutral-700 [&_h3]:mt-4 [&_h3]:mb-1
              [&_p]:text-sm [&_p]:leading-relaxed [&_p]:my-1.5
              [&_ul]:pl-5 [&_ul]:my-1 [&_li]:text-sm [&_li]:my-0.5
              [&_strong]:text-neutral-900
              ${editing ? 'min-h-[300px] bg-purple-50/20 ring-1 ring-inset ring-purple-200 rounded-md mx-2 my-2' : ''}`}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 bg-neutral-50 border-t border-neutral-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-1.5 text-xs font-medium text-neutral-600 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            {error && (
              <span className="text-xs text-red-600">{error}</span>
            )}
          </div>
          {sent ? (
            <div className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg">
              <CheckCircle className="w-3.5 h-3.5" />
              Email envoyé
            </div>
          ) : (
            <button
              onClick={handleSend}
              disabled={!emailTo || ccError !== null || sending}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? 'Envoi...' : 'Envoyer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
